import { User } from '../../entities/user';
import { UserRoles } from '../../entities/user-roles';
import { CurafidaSegmentItem } from '../../../common/entities/curafida-segment.item';
import { SegmentType } from '../../../common/entities/segment.type';
import { CurafidaFeatureEnum } from '../../../config/entities/base-page-config';
import { IconId } from '../../../common/entities/icon_type';
import { ConfigurationDetailFeatures } from '../../../config/resources/configuration.resource';
import { FeatureConfigs } from '../../../common/entities/curafida-frontend-configuration';

export class UserDetailTabs {
    private segmentItems: CurafidaSegmentItem<SegmentType>[] = [];

    constructor(
        private readonly featureConfigs: FeatureConfigs,
        private readonly features: ConfigurationDetailFeatures,
        private readonly loggedInUser: User,
    ) {}

    items(): CurafidaSegmentItem<SegmentType>[] {
        return this.segmentItems;
    }

    defaultSegment(): SegmentType {
        return (
            this.segmentItems.find((item) => item.value === SegmentType.DOSSIER)?.value ?? this.segmentItems[0]?.value
        );
    }

    /** The tabs of the record that its side panel offers as well, in the panel's order; only those the user may see. */
    sidePanelItems(): CurafidaSegmentItem<SegmentType>[] {
        return [SegmentType.MAIN_INFORMATION, SegmentType.NOTE, SegmentType.TASK]
            .map((segmentType) => this.segmentItems.find((item) => item.value === segmentType))
            .filter((item) => !!item);
    }

    init(args: {
        user: User;
        isMemberOfTenant: boolean;
        isMemberAllowToSeeOrder?: boolean;
        isMemberAllowToSeeConsultation?: boolean;
    }): CurafidaSegmentItem<SegmentType>[] {
        this.segmentItems = [];
        const isPatient = !!args.user?.roles?.includes(UserRoles.PATIENT);
        const isTenantPatient = isPatient && args.isMemberOfTenant;

        this.segmentItems.push(
            new CurafidaSegmentItem({
                name: 'PATIENT_ACCOUNT',
                icon: IconId.PERSON,
                value: SegmentType.MAIN_INFORMATION,
            }),
        );
        if (isTenantPatient) {
            this.addSegment({
                feature: CurafidaFeatureEnum.user,
                userRoles: [UserRoles.manage_patient_dossier, UserRoles.read_medical_history],
                blockedUserRoles: [UserRoles.ANALYST],
                name: 'ACT',
                iconName: IconId.CALENDAR,
                segmentType: SegmentType.DOSSIER,
            });
            this.addSegment({
                configuration: CurafidaFeatureEnum.note,
                userRoles: [UserRoles.read_note, UserRoles.SUPERVISOR],
                name: 'NOTE.PLURAL',
                iconName: IconId.NOTES,
                segmentType: SegmentType.NOTE,
            });
            this.addSegment({
                configuration: CurafidaFeatureEnum.course,
                userRoles: [UserRoles.manage_my_courses_and_meetings, UserRoles.manage_therapy],
                name: 'APPOINTMENT_PLAN',
                iconName: IconId.GROUP,
                segmentType: SegmentType.APPOINTMENTS,
            });
            this.addSegment({
                configuration: CurafidaFeatureEnum.program,
                userRoles: [UserRoles.read_therapy],
                name: 'PROGRAM.PLURAL',
                iconName: IconId.STAR,
                segmentType: SegmentType.PROGRAM,
            });
            this.addSegment({
                configuration: CurafidaFeatureEnum.task,
                userRoles: [UserRoles.read_therapy, UserRoles.SUPERVISOR, UserRoles.analyse_task],
                name: 'TASK_PLAN',
                iconName: IconId.TASKS,
                segmentType: SegmentType.TASK,
            });
            this.addSegment({
                configuration: CurafidaFeatureEnum.training,
                userRoles: [UserRoles.read_therapy],
                name: 'TRAINING.SINGULAR',
                iconName: IconId.TRAINING,
                segmentType: SegmentType.TRAINING,
            });
            this.addSegment({
                feature: CurafidaFeatureEnum.medication,
                userRoles: [UserRoles.read_medication],
                name: 'MEDICATION.SINGULAR',
                iconName: IconId.MEDICATION,
                segmentType: SegmentType.MEDICATION,
            });
            this.addSegment({
                feature: CurafidaFeatureEnum.measurement,
                userRoles: [UserRoles.read_measurement],
                name: 'MEASUREMENT.PLURAL',
                iconName: IconId.HEARTBEAT,
                segmentType: SegmentType.MEASUREMENT,
            });
            this.addSegment({
                feature: CurafidaFeatureEnum.screening,
                userRoles: [UserRoles.read_therapy],
                name: 'SCREENING.NAME',
                iconName: IconId.TRAIL,
                segmentType: SegmentType.SCREENING,
            });
        }
        if (args.user?.roles?.includes(UserRoles.SUPERVISOR)) {
            this.addSegment({
                feature: CurafidaFeatureEnum.user,
                userRoles: [UserRoles.USER_MANAGER],
                name: 'SUPERVISED_PATIENTS',
                iconName: IconId.PATIENTS,
                segmentType: SegmentType.SUPERVISED_PATIENTS,
            });
        }
        if (isPatient && args.isMemberAllowToSeeConsultation) {
            this.addSegment({
                feature: CurafidaFeatureEnum.consultation,
                userRoles: [UserRoles.read_consultation],
                name: 'CONSULTATION.PLURAL',
                iconName: IconId.DOCUMENTS,
                segmentType: SegmentType.CONSULTATION,
            });
        }
        if (isPatient && args.isMemberAllowToSeeOrder) {
            this.addSegment({
                feature: CurafidaFeatureEnum.order,
                userRoles: [UserRoles.read_order],
                name: 'ORDER.PLURAL',
                iconName: IconId.FILE,
                segmentType: SegmentType.ORDER,
            });
        }

        return this.segmentItems;
    }

    private addSegment(segment: Parameters<typeof CurafidaSegmentItem.addSegmentElementToList>[0]): void {
        CurafidaSegmentItem.addSegmentElementToList(
            segment,
            this.segmentItems,
            this.featureConfigs,
            this.features,
            this.loggedInUser,
        );
    }
}
